/**
 * UI-008 — THE LIVE RACE FOLD.
 *
 * Howard, 16 Aug 2026: *"during a race i want to be able to click on a driver
 * card and have it open into their whole stat card modal."*
 *
 * `DriverDossier` already existed, but it could only be built from a whole
 * `CareerSeason`. A standalone race has no season fold, no finance run and no
 * winter — it has the card, the tape, and a cursor sitting somewhere in it.
 * This module turns those three into the one section the dossier did not have:
 * **this race, so far**.
 *
 * ── THE CURSOR IS THE WHOLE POINT ──────────────────────────────────────────
 *
 * `foldRace` takes `turns` and a `cursor` and reads `turns.slice(0, cursor)`.
 * That single line is the rule: the fold physically cannot see a turn that has
 * not been played, because the turns after the cursor never enter the loop.
 * Open the dossier while rewound to turn 12 and it reports turn 12 — not
 * because a filter remembered to exclude turn 40, but because turn 40 was never
 * in the input.
 *
 * This is the same fault UI-001's ledger-scrubbing fix already killed once, and
 * the reason it is worth killing the same way twice is that the *other* shape —
 * fold the whole race, then subtract — is wrong in a way that looks right on
 * every screen except the one where it matters. `raceDossier.test.ts` asserts
 * the property directly: the fold over the full tape at cursor C is deep-equal
 * to the fold over a tape truncated at C.
 *
 * ── NOTHING IS COMPUTED THAT THE ENGINE ALREADY SAYS ────────────────────────
 *
 * Position, chips, laps down and turns led are read off the `Car` in the state
 * the cursor points at. They are not re-derived, because the engine already
 * decided them and a second opinion is just a second place to be wrong.
 *
 * What IS folded here is the ledger: escapes, duels, challenges, chip movements
 * and the incident list. Those are read exactly the way `standings.ts` reads
 * them for the season — group envelopes descended into, `trouble.checks` and
 * `challenge.sequence.challenges` treated as the per-driver rows they are — so
 * that the dossier and the championship can never disagree about what happened.
 * A driver who wants to know whether HE got out of a twelve-car wreck is not
 * served by a row that says twelve cars were in it.
 *
 * ── WHAT IS DELIBERATELY NOT CLAIMED ────────────────────────────────────────
 *
 * `chipsEarned` and `chipsSpent` are a fold over the ledger's own `ChipDelta`
 * rows. They do **not** reconcile to the hand a driver is holding, and this
 * module does not pretend they do: the cap of two pushes overflow into reserve
 * (D-EZ-28), a trip to the BOTTOM group forfeits ordinary chips (RB/xv), and
 * reserve chips are destroyed by losing track position without any ledger row
 * being written for it. So movements and balances are reported as two separate
 * things, each true, rather than as one number that would have to be false.
 */

import type { Car, RaceState } from '../domain/types.ts';
import type { RuleEvent } from '../domain/ruleEvent.ts';
import type { TurnResult } from '../domain/turn.ts';
import { heldChipsOf, reserveChipsOf, traitChipsOf, totalChipsOf } from '../domain/chips.ts';

// ---------------------------------------------------------------------------
// What a fold produces
// ---------------------------------------------------------------------------

/** One thing that happened to this driver, with the rule that made it happen. */
export interface RaceIncident {
  turn: number;
  /** Coarse kind, as the engine filed it: `trouble`, `duel`, `problem`, … */
  eventType: string;
  /** Fine story beat: `trouble.escaped`, `duel.win`, `challenge.pass`, … */
  momentTag: string;
  /** Never empty. `raceDossier.test.ts` asserts it. */
  ruleCitation: string;
  /** The engine's own sentence, about THIS driver where one exists. */
  description: string;
  /**
   * Where the driver went, when this event moved him. `null` = out of the race
   * or a slot the rule could not name; absent = it did not move him.
   */
  from?: number;
  to?: number | null;
}

/** A chip that moved, and the reason the ledger gave. */
export interface ChipMovement {
  turn: number;
  delta: number;
  reason: string;
}

/** The chips a driver is holding right now, in the four piles that exist. */
export interface ChipHand {
  /** Spendable this instant. Capped at 2 by D-EZ-28. */
  active: number;
  /** How many of `active` came from a driver trait (FN, ST). */
  trait: number;
  /**
   * Trait chips parked because he is in the BOTTOM or LAP DOWN group —
   * "The Performance Chip is not lost" (EZRA-S2 §4.0, D-EZ-26). Held but
   * unspendable, which is a thing worth SEEING rather than inferring from a
   * chip that quietly stopped working.
   */
  held: number;
  /** Chips in excess of two (D-EZ-28). No value until activated. */
  reserve: number;
  /** All of it. */
  total: number;
}

export interface DriverRaceFold {
  driverId: string;
  /** The cursor this fold was taken at. 0 = the grid, before the green. */
  cursor: number;
  /** The turn number showing on the boards at that cursor. */
  turnShowing: number;
  /** Where he lined up. */
  startPosition: number | null;
  /** Where he is at the cursor, or where he was when he went out. */
  position: number | null;
  /** Positions made up on his start. Negative = lost. */
  gained: number | null;
  status: Car['status'];
  outReason?: Car['outReason'];
  outTurn?: number;
  lapsDown: boolean;
  chips: ChipHand;
  turnsLed: number;
  consecutiveTurnsLed: number;
  /** Best and worst he has run at any cursor up to this one. */
  bestPosition: number | null;
  worstPosition: number | null;
  /** Every TROUBLE escape roll he was asked to make. */
  troubleAttempts: number;
  troubleEscapes: number;
  troubleChipSaves: number;
  troubleCollected: number;
  /** null until he has been asked once — not 0%, which would read as failure. */
  escapePct: number | null;
  duelsInstigated: number;
  duelsWon: number;
  duelsLost: number;
  /** Duels called ON him. */
  duelsDefended: number;
  challengesMade: number;
  challengesWon: number;
  /**
   * Calls that never reached a defender — voided on triples (RB/vi) or on a
   * triangle car's red 6 (RB/xvi), or aimed at a group with nobody in it.
   *
   * Counted SEPARATELY, and the counting harness is why. Folding them in with
   * real contests made `challenges made` exceed `challenges faced` across the
   * field by exactly this number, which looked like a bug in the fold and was
   * not: a voided call has a challenger and no defender, because the dice
   * killed it before it got to one. Filing it as a lost challenge would say he
   * was beaten by a man he never actually raced.
   */
  challengesVoided: number;
  challengesDefended: number;
  challengeDefencesHeld: number;
  chipsEarned: number;
  chipsSpent: number;
  chipMovements: ChipMovement[];
  incidents: RaceIncident[];
}

// ---------------------------------------------------------------------------
// Reading the group envelopes
// ---------------------------------------------------------------------------

/**
 * A TROUBLE event is a GROUP event carrying one `checks` row per car that had
 * to roll. `standings.ts` reads it the same way, and for the same reason: every
 * escape roll in the game — inside a duel, inside a feud, off the RACE CAM —
 * comes through the trouble module, so this is the only place the count is
 * complete.
 */
interface TroubleCheckShape extends RuleEvent {
  driverId: string;
  escaped?: boolean;
  collected?: boolean;
  chipSpent?: boolean;
}

interface TroubleShape {
  checks?: unknown;
}

/** A duel names exactly two drivers and says which of them won. */
interface DuelShape {
  instigatorId?: string;
  targetId?: string | null;
  declined?: boolean;
  won?: boolean;
}

/** A challenge sequence carries one row per attempt made inside it. */
interface ChallengeShape extends RuleEvent {
  challengerId?: string;
  defenderId?: string | null;
}

interface SequenceShape {
  challenges?: unknown;
}

interface ChipDeltaShape {
  driverId?: string;
  delta?: number;
  reason?: string;
}

interface WithChipDeltas {
  chipDeltas?: unknown;
}

function checksOf(event: RuleEvent): TroubleCheckShape[] {
  const raw = (event as unknown as TroubleShape).checks;
  return Array.isArray(raw) ? (raw as TroubleCheckShape[]) : [];
}

function challengesOf(event: RuleEvent): ChallengeShape[] {
  const raw = (event as unknown as SequenceShape).challenges;
  return Array.isArray(raw) ? (raw as ChallengeShape[]) : [];
}

function chipDeltasOf(event: RuleEvent): ChipDeltaShape[] {
  const raw = (event as unknown as WithChipDeltas).chipDeltas;
  return Array.isArray(raw) ? (raw as ChipDeltaShape[]) : [];
}

/** The position delta this event recorded for one driver, if any. */
function deltaFor(event: RuleEvent, driverId: string): { from: number; to: number | null } | null {
  const d = event.positionDeltas.find((p) => p.driverId === driverId);
  return d ? { from: d.from, to: d.to } : null;
}

function incident(turn: number, event: RuleEvent, driverId: string): RaceIncident {
  const moved = deltaFor(event, driverId);
  return {
    turn,
    eventType: event.eventType,
    momentTag: event.momentTag,
    ruleCitation: event.ruleCitation,
    description: event.description,
    ...(moved ? { from: moved.from, to: moved.to } : {}),
  };
}

// ---------------------------------------------------------------------------
// The fold
// ---------------------------------------------------------------------------

/** Everything the fold needs. A race, not a season. */
export interface RaceFoldSource {
  /** The grid as it lined up, before a turn was run. */
  start: RaceState;
  /** Every turn the engine produced — the WHOLE tape. */
  turns: readonly TurnResult[];
  /** How many of them the boards are showing. */
  cursor: number;
}

/** Find a car in a state, running or retired. */
function carIn(state: RaceState, driverId: string): Car | undefined {
  return (
    state.grid.find((c) => c.card.driverId === driverId) ??
    state.retired.find((c) => c.card.driverId === driverId)
  );
}

/**
 * Fold one driver's race up to the cursor.
 *
 * Returns `null` when the driver was never in this race at all, which is a
 * different fact from "has done nothing yet" and is left for the caller to say.
 */
export function foldRace(source: RaceFoldSource, driverId: string): DriverRaceFold | null {
  const { start, turns, cursor } = source;

  // THE LINE THAT MAKES THE CURSOR REAL. Everything below reads `played`, and
  // `played` cannot contain a turn the boards have not shown.
  const played = turns.slice(0, Math.max(0, Math.min(cursor, turns.length)));

  const stateNow = played.length > 0 ? played[played.length - 1].state : start;
  const car = carIn(stateNow, driverId) ?? carIn(start, driverId);
  if (!car) return null;

  const startCar = carIn(start, driverId);
  const startPosition = startCar ? startCar.position : null;

  const fold: DriverRaceFold = {
    driverId,
    cursor: played.length,
    turnShowing: stateNow.turn,
    startPosition,
    position: car.status === 'out' ? null : car.position,
    gained: null,
    status: car.status,
    ...(car.outReason ? { outReason: car.outReason } : {}),
    ...(car.outTurn !== undefined ? { outTurn: car.outTurn } : {}),
    lapsDown: car.lapsDown,
    chips: {
      active: Math.max(0, car.chips),
      trait: traitChipsOf(car),
      held: heldChipsOf(car),
      reserve: reserveChipsOf(car),
      total: totalChipsOf(car),
    },
    turnsLed: car.turnsLed,
    consecutiveTurnsLed: car.consecutiveTurnsLed,
    bestPosition: null,
    worstPosition: null,
    troubleAttempts: 0,
    troubleEscapes: 0,
    troubleChipSaves: 0,
    troubleCollected: 0,
    escapePct: null,
    duelsInstigated: 0,
    duelsWon: 0,
    duelsLost: 0,
    duelsDefended: 0,
    challengesMade: 0,
    challengesWon: 0,
    challengesVoided: 0,
    challengesDefended: 0,
    challengeDefencesHeld: 0,
    chipsEarned: 0,
    chipsSpent: 0,
    chipMovements: [],
    incidents: [],
  };

  if (startPosition !== null && fold.position !== null) fold.gained = startPosition - fold.position;

  // Best and worst include the grid itself: a pole-sitter who has led every turn
  // has a best of 1 from before the green, and saying otherwise would be a lie
  // by omission.
  const seen: number[] = [];
  if (startPosition !== null) seen.push(startPosition);
  for (const t of played) {
    const c = t.state.grid.find((x) => x.card.driverId === driverId);
    if (c) seen.push(c.position);
  }
  if (seen.length > 0) {
    fold.bestPosition = Math.min(...seen);
    fold.worstPosition = Math.max(...seen);
  }

  for (const t of played) {
    for (const event of t.events) {
      // --- chips: every row that names him, whatever event carried it -------
      for (const d of chipDeltasOf(event)) {
        if (d.driverId !== driverId || typeof d.delta !== 'number' || d.delta === 0) continue;
        fold.chipMovements.push({ turn: t.turn, delta: d.delta, reason: d.reason ?? event.momentTag });
        if (d.delta > 0) fold.chipsEarned += d.delta;
        else fold.chipsSpent += -d.delta;
      }

      // --- TROUBLE: descend to HIS check, not the group's headline ----------
      if (event.eventType === 'trouble') {
        for (const check of checksOf(event)) {
          if (check.driverId !== driverId) continue;
          fold.troubleAttempts++;
          if (check.escaped === true) fold.troubleEscapes++;
          if (check.chipSpent === true) fold.troubleChipSaves++;
          if (check.collected === true) fold.troubleCollected++;
          fold.incidents.push(incident(t.turn, check, driverId));
        }
        continue;
      }

      // --- a challenge sequence is a wrapper; the attempts are the rows -----
      if (event.eventType === 'quality-challenge-sequence') {
        for (const ch of challengesOf(event)) {
          const isChallenger = ch.challengerId === driverId;
          const isDefender = ch.defenderId === driverId;
          if (!isChallenger && !isDefender) continue;
          // `challenge.pass` is the challenger getting by, `chip-win` is him
          // buying it. Every other tag is the defender holding, or the call
          // dying before it reached one.
          const passed = ch.momentTag === 'challenge.pass' || ch.momentTag === 'challenge.chip-win';
          const voided =
            ch.momentTag.startsWith('challenge.void') || ch.momentTag === 'challenge.no-defender';
          if (isChallenger) {
            fold.challengesMade++;
            if (voided) fold.challengesVoided++;
            else if (passed) fold.challengesWon++;
          }
          if (isDefender) {
            fold.challengesDefended++;
            if (!passed) fold.challengeDefencesHeld++;
          }
          fold.incidents.push(incident(t.turn, ch, driverId));
        }
        continue;
      }

      // --- duels. Declined duels are not fights; standings.ts skips them ----
      if (event.eventType === 'duel') {
        const duel = event as unknown as DuelShape;
        const instigated = duel.instigatorId === driverId;
        const targeted = duel.targetId === driverId;
        if (!instigated && !targeted) continue;
        if (duel.declined !== true) {
          if (instigated) {
            fold.duelsInstigated++;
            if (duel.won === true) fold.duelsWon++;
            else fold.duelsLost++;
          }
          if (targeted) fold.duelsDefended++;
        }
        fold.incidents.push(incident(t.turn, event, driverId));
        continue;
      }

      // --- everything else, when it names him ------------------------------
      // RACE CAM carries no actors by design: it is the call, not the result.
      if (event.actorIds.includes(driverId)) {
        fold.incidents.push(incident(t.turn, event, driverId));
      }
    }
  }

  if (fold.troubleAttempts > 0) fold.escapePct = fold.troubleEscapes / fold.troubleAttempts;

  return fold;
}

/**
 * The whole field folded at one cursor, keyed by driver id.
 *
 * Used by the counting harness rather than by the modal, which only ever wants
 * one driver — but a harness that folds one driver at a time cannot check a
 * field-wide closed form, and those are the checks that have actually caught
 * things in this project.
 */
export function foldField(source: RaceFoldSource): Map<string, DriverRaceFold> {
  const out = new Map<string, DriverRaceFold>();
  const ids = new Set<string>();
  for (const c of source.start.grid) ids.add(c.card.driverId);
  for (const c of source.start.retired) ids.add(c.card.driverId);
  for (const id of ids) {
    const fold = foldRace(source, id);
    if (fold) out.set(id, fold);
  }
  return out;
}

/**
 * THE FIELD IN THE ORDER THE BOARDS ARE SHOWING IT, at this cursor.
 *
 * Howard, 17 Aug 2026: *"when this driver card is up in a race can you add a
 * left arrow and right arrow so they can increment up and down the driver cards
 * without having to close them and reopen another one"*.
 *
 * ⚠ THE ORDER IS READ AT THE CURSOR, not off the starting grid, and that is the
 * whole point. The dossier is a view of the moment the boards are on — rewind
 * to turn 12 and every number in it walks back to turn 12 — so stepping ‹ and ›
 * has to walk the running order AT TURN 12. Walking the grid instead would send
 * the player through the field in an order the screen behind the modal is not
 * in, which reads as a bug even though every card would be correct.
 *
 * RETIRED CARS COME LAST, in the order they are held. They are still openable
 * from the boards — a driver who is out has a dossier worth reading — so
 * leaving them out would make ‹ › skip a card the player can see.
 */
export function runningOrder(source: RaceFoldSource): string[] {
  const { start, turns, cursor } = source;
  const played = turns.slice(0, Math.max(0, Math.min(cursor, turns.length)));
  const stateNow = played.length > 0 ? played[played.length - 1].state : start;

  const running = [...stateNow.grid]
    .sort((a, b) => a.position - b.position)
    .map((c) => c.card.driverId);
  const seen = new Set(running);

  const out = [...running];
  for (const car of stateNow.retired) {
    const id = car.card.driverId;
    if (!seen.has(id)) {
      seen.add(id);
      out.push(id);
    }
  }
  // A car that is in neither list at this cursor but WAS on the starting grid —
  // it cannot be opened from the boards, but including it costs nothing and
  // means the list is the whole field rather than nearly the whole field.
  for (const car of start.grid) {
    const id = car.card.driverId;
    if (!seen.has(id)) {
      seen.add(id);
      out.push(id);
    }
  }
  return out;
}
